import useUserGroups from '../../utils/react/hooks/useUserGroups';
import { getCurrentUserId } from '../auth';
import { cognitoApi } from '../cognitoApi';
import { useGetStaffQuery } from '../staff';
import { useGetStudentQuery } from '../students';
import { CognitoAPIResponse } from '../types';
import { attachIds } from '../utils';

import { UserAccountStatus, UserNote } from './types';

// Define a service using a base URL and expected endpoints
export const usersApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    updateUser: build.mutation<any, {userId: string, newAttributes: any}>({
      query(arg) {
        const { userId, newAttributes } = arg;
        return {
          url: `users/${userId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Invalidates all queries that subscribe to this User
      invalidatesTags: (result, error, arg: {userId: string, newAttributes: any}) => [
        { type: 'Users', id: arg.userId },
        { type: 'Students', id: arg.userId },
        { type: 'Staff', id: arg.userId },
      ],
    }),
    disableUser: build.mutation<any, {userId: string}>({
      query(arg) {
        const { userId } = arg;
        return {
          url: `users/${userId}/disable`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Users', id: arg?.userId },
        { type: 'Students', id: arg?.userId },
        { type: 'Staff', id: arg?.userId },
      ],
    }),
    enableUser: build.mutation<any, {userId: string}>({
      query(arg) {
        const { userId } = arg;
        return {
          url: `users/${userId}/enable`,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Users', id: arg?.userId },
        { type: 'Students', id: arg?.userId },
        { type: 'Staff', id: arg?.userId },
      ],
    }),
    getProfileImageUrl: build.query<CognitoAPIResponse<string>, {userId: string}>({
      query: (arg) => `users/${arg.userId}/profileImageUrl`,
      providesTags: (result, error, arg) => [
        { type: 'Users', id: arg.userId },
        { type: 'Students', id: arg.userId },
        { type: 'Staff', id: arg.userId },
      ],
    }),
    getUserNotes: build.query<CognitoAPIResponse<UserNote[]>, {
      active?: boolean,
      dueBeforeDatetime?: number,
    }>({
      query(arg) {
        const { active, dueBeforeDatetime } = arg;

        return {
          url: 'users/notes/query',
          params: { active, dueBeforeDatetime },
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'UserNotes' as const, id })), 'UserNotes']
        : ['UserNotes']),
      transformResponse(response: any) {
        return attachIds(response, (entry: UserNote) => `${entry.userId}#${entry.creationDatetime}`);
      },
    }),
    getUserStatus: build.query<CognitoAPIResponse<UserAccountStatus>, {userId: string}>({
      query(arg) {
        return {
          url: `users/${arg.userId}/status`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'Users', id: arg.userId },
        { type: 'Students', id: arg.userId },
        { type: 'Staff', id: arg.userId },
      ],
    }),
    resetTemporaryPassword: build.mutation<any, {userId: string}>({
      query(arg) {
        const { userId } = arg;
        return {
          url: `users/${userId}/password/reset`,
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Users', id: arg.userId },
      ],
    }),
    updateUserMfaMethod: build.mutation<any, {
      userId: string;
      mfaMethod: string,
      isTotpSetup?: boolean
    }>({
      query(arg) {
        const { userId, mfaMethod, isTotpSetup } = arg;
        return {
          url: `users/${userId}/mfa/update`,
          body: { mfaMethod, isTotpSetup },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Users', id: arg.userId },
        { type: 'Students', id: arg.userId },
        { type: 'Staff', id: arg.userId },
      ],
    }),
  }),
});

export const { useUpdateUserMutation,
  useDisableUserMutation,
  useEnableUserMutation,
  useLazyGetProfileImageUrlQuery,
  useResetTemporaryPasswordMutation,
  useGetUserStatusQuery,
  useGetUserNotesQuery,
  useUpdateUserMfaMethodMutation } = usersApi;

// Conditionally determine whether the user is a student or staff member,
// then make the relevant call
export const useGetCurrentUserQuery = (opts?: any) => {
  const groups = useUserGroups() || [];
  if (groups.includes('Students')) {
    return useGetStudentQuery({ studentId: getCurrentUserId() || '' }, { skip: groups.length === 0, ...opts });
  }
  return useGetStaffQuery({ staffId: getCurrentUserId() || '' }, { skip: groups.length === 0, ...opts });
};
